import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 실명 입력 및 노출 동의 모달.
 * 정회원 이상 가입/업그레이드 전, 또는 기존 정회원의 프로필 보완 시 사용.
 * 저장 성공 시 onConfirmed(updatedUser) 호출.
 */
export function RealNameConsentModal({
  open,
  onClose,
  onConfirmed,
  title = "정회원 멤버십 가입 안내",
  confirmLabel = "동의하고 계속하기",
  dismissible = true,
}) {
  const { user, token, updateUser } = useAuth();
  const [realName, setRealName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRealName((user?.real_name || user?.name || "").trim());
      setAgreed(false);
    }
  }, [open, user]);

  if (!open) return null;

  const canSubmit = realName.trim().length >= 2 && agreed && !saving;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ real_name: realName.trim(), real_name_consent: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "저장에 실패했습니다.");
      updateUser(data);
      toast.success("실명 정보가 저장되었어요");
      onConfirmed?.(data);
    } catch (e) {
      toast.error(e.message || "저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="real-name-modal">
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-stone-100">
          <h2 className="font-serif text-lg font-bold text-stone-900">{title}</h2>
          {dismissible && (
            <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="real-name-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-5">
          {/* 안내 박스 */}
          <div className="bg-burgundy-50 border border-burgundy-100 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-burgundy-600" />
              <h3 className="text-[13px] font-bold text-burgundy-800">실명 입력 및 노출 안내</h3>
            </div>
            <p className="text-[12.5px] text-stone-600 leading-relaxed">
              정회원 이상은 신뢰와 안전한 만남을 위해 실명 입력이 필수입니다.
              커뮤니티 글쓰기와 댓글에서는 아이디 또는 닉네임이 표시되며,
              회원보기 및 프로필 상세보기에서는 실명이 표시됩니다.
              이는 검증된 회원 간의 신뢰 있는 만남을 위한 운영 원칙입니다.
            </p>
          </div>

          {/* 실명 입력 */}
          <div>
            <label className="block text-sm font-semibold text-stone-800 mb-1.5">
              실명 (성명) <span className="text-burgundy-600">*</span>
            </label>
            <input
              type="text"
              value={realName}
              onChange={(e) => setRealName(e.target.value)}
              placeholder="실명을 입력해주세요"
              className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-burgundy-400 focus:ring-2 focus:ring-burgundy-100 outline-none text-[15px] text-stone-900"
              data-testid="real-name-input"
            />
            <p className="text-[11px] text-stone-400 mt-1.5">
              정회원 이상은 신뢰와 안전한 만남을 위해 실명 입력이 필수입니다.
            </p>
          </div>

          {/* 동의 체크박스 */}
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-burgundy-600 shrink-0"
              data-testid="real-name-consent-checkbox"
            />
            <span className="text-[12.5px] text-stone-600 leading-snug">
              위 내용을 확인했으며, 정회원 이상 이용 시 실명이 회원보기와 프로필에 표시되는 것에 동의합니다.
            </span>
          </label>

          <button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="w-full px-5 py-3.5 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="real-name-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default RealNameConsentModal;
